import { useEventListener, usePreferredReducedMotion, useWindowScroll, useWindowSize } from '@vueuse/core'

export const LONG_PAGE_VIEWPORTS = 4
export const SHOW_AFTER_VIEWPORTS = 1

export function isBackToTopVisible(
  scrollY: number,
  viewportHeight: number,
  pageHeight: number,
): boolean {
  if (viewportHeight <= 0)
    return false

  const isLongPage = pageHeight >= viewportHeight * LONG_PAGE_VIEWPORTS
  const hasScrolledPastFirstScreen = scrollY >= viewportHeight * SHOW_AFTER_VIEWPORTS
  return isLongPage && hasScrolledPastFirstScreen
}

export function resolveScrollToTopBehavior(prefersReducedMotion: boolean): ScrollBehavior {
  return prefersReducedMotion ? 'auto' : 'smooth'
}

export function scrollWindowToTop(prefersReducedMotion: boolean): void {
  if (!import.meta.client)
    return

  window.scrollTo({
    top: 0,
    behavior: resolveScrollToTopBehavior(prefersReducedMotion),
  })
}

function readPageHeight(): number {
  if (!import.meta.client)
    return 0

  return document.documentElement.scrollHeight
}

export function useBackToTop() {
  const route = useRoute()
  const { y } = useWindowScroll()
  const { height } = useWindowSize()
  const preferredMotion = usePreferredReducedMotion()
  const pageHeight = shallowRef(0)

  function measurePageHeight() {
    pageHeight.value = readPageHeight()
  }

  const isVisible = computed(() =>
    isBackToTopVisible(y.value, height.value, pageHeight.value),
  )

  function scrollToTop() {
    scrollWindowToTop(preferredMotion.value === 'reduce')
  }

  useEventListener('scroll', measurePageHeight, { passive: true })
  useEventListener('resize', measurePageHeight)
  watch(() => route.fullPath, () => {
    nextTick(measurePageHeight)
  })
  onMounted(measurePageHeight)

  return { isVisible, scrollToTop }
}
